// @ts-check
'use_strict';
import { ToggleContent } from '../tv_utils/toggle_content';
export declare var $;

/**
 * Handles interactions with the carrier location elements.
 */

export class CarrierLocationSelector {
    /**
     * @var {number} active_tab_idx to track which carrier tab is active.
     * @var {{[key: string]: any}} elements that are relevant on the DOM.
     * @var {ToggleContent} tabs for each carrier with their options.
     * @var {number} throttle reference to set timeout to stop change events from firing too fast.
     * @var {string} page that initialized the selector.
     * @var {Function} render_callback Optional function callback for when the view is done rendering.
     */
    active_tab_idx: number;
    elements: { [key: string]: any; };
    tabs: ToggleContent;
    throttle: number;
    page: string;
    render_callback: Function;

    /**
     * Instantiate the class and create references to the elements on the DOM.
     *
     * @param {Function} render_callback to call after the controls are rendered.
     * @param {string} page the selector is used on.
     * @return {CarrierLocationSelector} instantiated and bound.
     */
    constructor(render_callback: Function = null, page: string = '') {
        this.elements = {};
        this.active_tab_idx = 0;
        this.tabs = null;
        this.page = page || '';
        this.render_callback = render_callback;
    }

    /**
     * Create the references and events on the results of a request for carrier locations.
     *
     * @return {void}
     */
    bind_results(): void {
        this.elements.results = {
            tab_content: document.getElementsByClassName('js-carrier-locations-tab-content'),
            buttons: {
                links: document.getElementsByClassName('js-carrier-locations-tablink'),
                mobile_links: document.getElementsByClassName('js-carrier-locations-mobile-tablink')
            }
        };
        this.tabs = new ToggleContent(
            this.elements.results.tab_content,
            this.elements.results.buttons,
            this.active_tab_idx,
            false
        );

        // Bind the zipcode input.
        this.elements.zipcode_input = document.getElementById('js-carrier-locations-zip-input');
        this.elements.zipcode_input.addEventListener('change', this.zipcode_input.bind(this));
        this.elements.zipcode_input.addEventListener('keyup', this.zipcode_input.bind(this));
    }

    /**
     * Handles keyup events on the zipcode input.
     *
     * @returns {void}
     */
    zipcode_input(): void {
        // Throttle user inputs so that if they are rapidly making changes to a control we don't send ajax too soon.
        if (this.throttle || this.elements.zipcode_input.value.length < 5) {
            clearTimeout(this.throttle);
        }

        if (this.elements.zipcode_input.value.length < 5) {
            return;
        }

        // ts-check thinks this is a NodeJS.Timer and webpack thinks it's a number so some casting to band-aid here.
        this.throttle = <number><any>setTimeout(
            function () {
                this.fetch_locations(this.elements.zipcode_input.value);
            }.bind(this),
            200
        );
    }

    /**
     * Fetches the list of carrier locations given the zip.
     *
     * @param {string} zip code to estimate from.
     * @return {void}
     */
    fetch_locations(zip: string): void {
        if (this.elements.zipcode_input) {
            this.elements.zipcode_input.disabled = 'disabled';
        }

        const url = `index.php?dispatch=tv_shipping.carrier_locations`;
        $.ceAjax('request', url, {
            method: 'POST',
            data: {
                zip: zip,
                page: this.page
            },
            caching: true,
            result_ids: 'js-carrier_locations_output',
            callback: this.fetch_carrier_locations_callback.bind(this)
        });
    }

    /**
     * Re-binds the results of a request to get carrier locations.
     *
     * @return {void}
     */
    fetch_carrier_locations_callback(): void {
        this.bind_results();

        this.elements.zipcode_input.disabled = '';

        if (this.render_callback) {
            this.render_callback();
        }
    }
}
